import * as React from 'react';
import { Card, CardContent } from '@/hen-ui/composites/Card';
import { Flex } from '@/hen-ui/composites/Flex';
import { Text } from '@/hen-ui/atomics/Text';
import { cn } from '@/hen-ui/utils';

export interface PropertyValueProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** Main data point title. Renders only if provided. */
  title?: string;
  /** Additional context below title. Renders only if provided. */
  detail?: string;
  /** Property label. Renders only if provided. */
  propertyTitle?: string;
  /** Main value displayed. */
  value: string | number;
  /** Percentage indicator (e.g., "+22%"), displayed in success-500. */
  variation?: string;
}

/**
 * PropertyValue - Composite component for data point display
 *
 * Displays a title/detail header section and a property/value section with
 * optional percentage variation indicator. Uses a gray card background with
 * rounded corners and supports toggling visibility of each text element by
 * omitting the corresponding prop.
 */
export const PropertyValue = React.forwardRef<
  HTMLDivElement,
  PropertyValueProps
>(
  (
    { title, detail, propertyTitle, value, variation, className, ...props },
    ref,
  ) => {
    const hasHeader = Boolean(title || detail);

    return (
      <Card
        ref={ref}
        variant='background-gray'
        className={cn('w-full rounded-xs', className)}
        {...props}
      >
        <CardContent className='gap-xs'>
          {hasHeader && (
            <Flex gap='xxs'>
              {title && (
                <Text variant='b4' color='heading' weight='bold'>
                  {title}
                </Text>
              )}
              {detail && (
                <Text variant='l1' color='default'>
                  {detail}
                </Text>
              )}
            </Flex>
          )}

          <Flex gap='xxs'>
            {propertyTitle && (
              <Text variant='l1' color='default'>
                {propertyTitle}
              </Text>
            )}
            <Flex direction='row' justify='between' gap='sm'>
              <Text variant='b4' color='heading' weight='bold'>
                {value}
              </Text>
              {variation && (
                <Text variant='b4' className='text-success-500' weight='bold'>
                  {variation}
                </Text>
              )}
            </Flex>
          </Flex>
        </CardContent>
      </Card>
    );
  },
);

PropertyValue.displayName = 'PropertyValue';
